// pages/strategic-playbook/tectonic/[slug].js
// ════════════════════════════════════════════════════════════════════
// Tectonic shift detail page (F13 — depth-pass redesign).
//
// Renders the full per-shift content including the v3 paid-tier depth
// fields: contrarian read, named positions, watch metrics, historical
// analogs, track record, lessons.
//
// Section order is deliberate — contrarian read comes early so the
// wedge against consensus is the load-bearing first read.
// ════════════════════════════════════════════════════════════════════

import Head from 'next/head';
import Link from 'next/link';
import {
  TECTONIC_SHIFTS,
  getTectonicShift,
  resolveShiftRef,
} from '../../../data/strategic-playbook/tectonic-shifts';
import { FindingCite } from '../../../features/findings/components-shared';
import {
  StageTimeline,
  DivergenceTimeline,
} from '../../../features/findings/charts';

export async function getStaticPaths() {
  return {
    paths: TECTONIC_SHIFTS.map((s) => ({ params: { slug: s.slug } })),
    fallback: false,
  };
}

export async function getStaticProps({ params }) {
  const shift = getTectonicShift(params.slug);
  if (!shift) return { notFound: true };
  const interactions = (shift.crossShiftInteractions || []).map((cx) => ({
    ...cx,
    target: resolveShiftRef(cx.otherShiftId),
  }));
  const analogs = (shift.historicalAnalogs || []).map((a) => ({
    ...a,
    target: resolveShiftRef(a.shiftId),
  }));
  return { props: { shift, interactions, analogs } };
}

const CATEGORY_LABEL = {
  historical: 'Historical · resolved',
  active:     'Active · navigating now',
  emerging:   'Emerging · early signals',
};

export default function TectonicShiftDetailPage({ shift, interactions, analogs }) {
  const isHistorical = shift.category === 'historical';

  return (
    <div className="sp-root">
      <Head>
        <title>{shift.name} — Tectonic Shift · Strategic Playbook · Shift</title>
        <meta
          name="description"
          content={`Tectonic shift detail: ${shift.name}. ${shift.note}`}
        />
      </Head>

      <div className="sp-page">
        <div className="findings-index-topbar">
          <Link href="/strategic-playbook/tectonic-shifts" className="finding-back">
            ← All tectonic shifts
          </Link>
          <div className="findings-index-topbar-meta">
            {CATEGORY_LABEL[shift.category] || 'Tectonic Shift'}{shift.era ? ` · ${shift.era}` : ''}
          </div>
        </div>

        <header className="tectonic-detail-header">
          <div className="finding-eyebrow">§ Tectonic shift · {shift.category}</div>
          <h1 className="tectonic-detail-title">{shift.name}</h1>
          <p className="tectonic-detail-lede">{shift.note}</p>

          <div className="tectonic-detail-stage-row">
            <div className="tectonic-detail-stage-label">
              {shift.stageLabel}{shift.era ? ` · ${shift.era}` : ''}
            </div>
            <div className="tectonic-detail-stage-bar">
              {shift.stages.map((stage, i) => {
                let cls = 'tectonic-detail-stage-cell';
                if (i < shift.currentStageIndex) cls += ' passed';
                if (i === shift.currentStageIndex) cls += ' active';
                return (
                  <div key={stage} className={cls}>
                    <span className="tectonic-detail-stage-cell-label">{stage}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </header>

        {/* ── §A Contrarian read — the wedge (highest-leverage section) ── */}
        {shift.contrarianRead ? (
          <Section eyebrow="§ The wedge — what we think vs consensus" title="Where the read diverges from the room.">
            <div className="tectonic-contrarian">
              <div className="tectonic-contrarian-row tectonic-contrarian-consensus">
                <div className="tectonic-contrarian-label">Consensus</div>
                <div className="tectonic-contrarian-body">{shift.contrarianRead.consensus}</div>
              </div>
              <div className="tectonic-contrarian-row tectonic-contrarian-lab">
                <div className="tectonic-contrarian-label">Our read</div>
                <div className="tectonic-contrarian-body">{shift.contrarianRead.lab}</div>
              </div>
              <div className="tectonic-contrarian-row tectonic-contrarian-wedge">
                <div className="tectonic-contrarian-label">The wedge</div>
                <div className="tectonic-contrarian-body">{shift.contrarianRead.wedge}</div>
              </div>
            </div>
          </Section>
        ) : (
          <Section eyebrow="§ The wedge — what we think vs consensus" title="Pending author input.">
            <p className="tectonic-prose tectonic-prose-empty">
              Contrarian read not yet authored for this shift. The wedge
              section will name the consensus position, our differing read,
              and the structural reason for the divergence.
            </p>
          </Section>
        )}

        {/* ── §1 Thesis ── */}
        <Section eyebrow="§ Thesis" title="What's actually shifting.">
          <p className="tectonic-prose tectonic-prose-lede">{shift.thesis}</p>
        </Section>

        {/* ── §1.5 Era chart — historical shifts only ── */}
        {isHistorical && shift.eraChart && (
          <Section
            eyebrow="§ The data underneath"
            title="Visualized."
          >
            <DivergenceTimeline
              title={shift.eraChart.title}
              series={shift.eraChart.series}
              xLabel={shift.eraChart.xLabel}
              yLabel={shift.eraChart.yLabel}
              yMin={shift.eraChart.yMin}
              yMax={shift.eraChart.yMax}
            />
            {shift.eraChart.subtitle && (
              <p className="tectonic-prose tectonic-chart-caption">
                {shift.eraChart.subtitle}
              </p>
            )}
          </Section>
        )}

        {/* ── §2 Stage history ── */}
        {shift.stageHistory?.length > 0 && (
          <Section eyebrow="§ Stage history" title={isHistorical ? 'How it played out.' : 'How it got here.'}>
            <StageTimeline
              stageHistory={shift.stageHistory}
              stages={shift.stages}
              currentStageIndex={shift.currentStageIndex}
            />
            <ol className="tectonic-history">
              {shift.stageHistory.map((h, i) => (
                <li key={i} className="tectonic-history-item">
                  <div className="tectonic-history-date">{h.date}</div>
                  <div className="tectonic-history-stage">{h.stage}</div>
                  <div className="tectonic-history-note">{h.note}</div>
                </li>
              ))}
            </ol>
          </Section>
        )}

        {/* ── §3 Asymmetric positions — categorical ── */}
        <Section eyebrow="§ Asymmetric positions — by category" title="Where the shift creates differential exposure.">
          <div className="tectonic-pair-grid">
            <div className="tectonic-pair-col tectonic-pair-up">
              <div className="tectonic-pair-eyebrow">Beneficiaries</div>
              <ul className="tectonic-list">
                {shift.beneficiaries.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            </div>
            <div className="tectonic-pair-col tectonic-pair-down">
              <div className="tectonic-pair-eyebrow">Trapped sectors</div>
              <ul className="tectonic-list">
                {shift.trappedSectors.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            </div>
          </div>
        </Section>

        {/* ── §4 Named positions — specific entities (paid-tier depth) ── */}
        <Section eyebrow="§ Named positions — specific entities" title="Where the categorical reads land in particular names.">
          {(shift.namedBeneficiaries?.length || shift.namedTrapped?.length) ? (
            <div className="tectonic-positions">
              {shift.namedBeneficiaries?.length > 0 && (
                <div className="tectonic-positions-block tectonic-positions-up">
                  <div className="tectonic-positions-header">Named beneficiaries</div>
                  {shift.namedBeneficiaries.map((p, i) => (
                    <PositionCard key={`b-${i}`} pos={p} kind="up" />
                  ))}
                </div>
              )}
              {shift.namedTrapped?.length > 0 && (
                <div className="tectonic-positions-block tectonic-positions-down">
                  <div className="tectonic-positions-header">Named trapped</div>
                  {shift.namedTrapped.map((p, i) => (
                    <PositionCard key={`t-${i}`} pos={p} kind="down" />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p className="tectonic-prose tectonic-prose-empty">
              Specific named positions not yet authored. This section will
              carry tickers / companies / asset-class names with thesis,
              risk, and sizing notes — the difference between a category
              read and a position read.
            </p>
          )}
        </Section>

        {/* ── §5 Signal tracking — descriptions ── */}
        <Section eyebrow="§ Signal tracking" title="What would tell you the shift is accelerating — or stalling.">
          <div className="tectonic-pair-grid">
            <div className="tectonic-pair-col tectonic-pair-up">
              <div className="tectonic-pair-eyebrow">Watch for (acceleration)</div>
              <ul className="tectonic-list">
                {shift.watchFor.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            </div>
            <div className="tectonic-pair-col tectonic-pair-down">
              <div className="tectonic-pair-eyebrow">Anti-watch-for (stalling / reversal)</div>
              <ul className="tectonic-list">
                {shift.antiWatchFor.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            </div>
          </div>
        </Section>

        {/* ── §6 Watch metrics — quantitative thresholds (paid-tier depth) ── */}
        {!isHistorical && (
          <Section eyebrow="§ Watch metrics — quantitative" title="Specific thresholds with current values.">
            {shift.watchMetrics?.length > 0 ? (
              <table className="tectonic-metrics">
                <thead>
                  <tr>
                    <th>Metric</th>
                    <th>Threshold</th>
                    <th>Current</th>
                    <th>Interpretation</th>
                  </tr>
                </thead>
                <tbody>
                  {shift.watchMetrics.map((m, i) => (
                    <tr key={i}>
                      <td>{m.metric}</td>
                      <td className="tectonic-metrics-threshold">{m.threshold}</td>
                      <td className="tectonic-metrics-current">
                        {m.currentValue}
                        {m.asOf && <span className="tectonic-metrics-asof"> · {m.asOf}</span>}
                      </td>
                      <td>{m.interpretation}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="tectonic-prose tectonic-prose-empty">
                Quantitative watch metrics not yet authored. This section
                will carry specific named metrics with their threshold
                levels and current values — the at-a-glance dashboard
                that turns a description into a tracker.
              </p>
            )}
          </Section>
        )}

        {/* ── §7 Historical analogs — pattern matching ── */}
        {!isHistorical && analogs?.length > 0 && (
          <Section eyebrow="§ Historical analogs" title="What past shifts can teach us about this one.">
            <div className="tectonic-analogs">
              {analogs.map((a, i) => (
                <div key={i} className="tectonic-analog">
                  <div className="tectonic-analog-head">
                    {a.target ? (
                      <Link href={`/strategic-playbook/tectonic/${a.target.slug}`} className="tectonic-analog-link">
                        <span className="tectonic-analog-name">{a.target.name}</span>
                        <span className="tectonic-analog-arrow">→</span>
                      </Link>
                    ) : (
                      <span className="tectonic-analog-name">{a.shiftId}</span>
                    )}
                    <span className="tectonic-analog-similarity">
                      Similarity {Math.round((a.similarity || 0) * 100)}%
                    </span>
                  </div>
                  <div className="tectonic-analog-difference">
                    <span className="tectonic-analog-diff-label">Key difference</span>
                    {a.keyDifference}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* ── §8 Lessons — historical only ── */}
        {isHistorical && shift.lessons?.length > 0 && (
          <Section eyebrow="§ Lessons for analog application" title="What this shift teaches about active shifts that resemble it.">
            <ol className="tectonic-lessons">
              {shift.lessons.map((l, i) => (
                <li key={i} className="tectonic-lesson">{l}</li>
              ))}
            </ol>
          </Section>
        )}

        {/* ── §9 Related findings ── */}
        <Section eyebrow="§ Related Lab findings" title="Where the mechanism is rigorously tested.">
          {shift.relatedFindings?.length > 0 ? (
            <ul className="tectonic-findings-list">
              {shift.relatedFindings.map((handle) => (
                <li key={handle} className="tectonic-findings-item">
                  <FindingCite handle={handle} />
                </li>
              ))}
            </ul>
          ) : (
            <p className="tectonic-prose tectonic-prose-empty">
              No Lab finding has been authored on this shift yet. The shift
              is tracked here as macro frame; rigorous mechanism testing
              comes when a finding is registered against the corpus.
            </p>
          )}
        </Section>

        {/* ── §10 Cross-shift interactions ── */}
        {interactions?.length > 0 && (
          <Section eyebrow="§ Cross-shift interactions" title="Where this shift compounds or conflicts with another.">
            <div className="tectonic-interactions">
              {interactions.map((cx, i) => (
                <div
                  key={i}
                  className={`tectonic-interaction tectonic-interaction-${cx.direction || 'compounding'}`}
                >
                  <div className="tectonic-interaction-head">
                    <span className="tectonic-interaction-direction">
                      {cx.direction === 'conflicting' ? '⚠ Conflicting' : '↗ Compounding'}
                    </span>
                    {cx.target ? (
                      <Link href={`/strategic-playbook/tectonic/${cx.target.slug}`} className="tectonic-interaction-link">
                        with {cx.target.name} →
                      </Link>
                    ) : (
                      <span>with {cx.otherShiftId}</span>
                    )}
                  </div>
                  <div className="tectonic-interaction-mechanism">
                    {cx.mechanism}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* ── §11 Track record — the credibility ledger ── */}
        {!isHistorical && (
          <Section eyebrow="§ Track record" title="Prior calls + outcomes for this shift.">
            {shift.trackRecord?.length > 0 ? (
              <ol className="tectonic-track">
                {shift.trackRecord.map((r, i) => (
                  <li key={i} className={`tectonic-track-item tectonic-track-${r.outcome}`}>
                    <div className="tectonic-track-date">{r.date}</div>
                    <div className="tectonic-track-outcome">{r.outcome}</div>
                    <div className="tectonic-track-call">{r.call}</div>
                    {r.notes && <div className="tectonic-track-notes">{r.notes}</div>}
                  </li>
                ))}
              </ol>
            ) : (
              <p className="tectonic-prose tectonic-prose-empty">
                No prior calls logged for this shift yet. The track record
                builds over time as predictions resolve. It&rsquo;s the
                credibility ledger — visible past calls and their outcomes,
                same way the Lab corpus tracks pre-registered predictions.
              </p>
            )}
          </Section>
        )}

        <footer className="findings-index-footer">
          <p>
            Tectonic shifts are macro frames. <Link href="/findings" className="methodology-inline-link">Lab findings</Link> are
            the rigorously-tested mechanisms underneath them. Browse all
            tracked shifts at <Link href="/strategic-playbook/tectonic-shifts" className="methodology-inline-link">/strategic-playbook/tectonic-shifts</Link>.
          </p>
        </footer>
      </div>
    </div>
  );
}

function Section({ eyebrow, title, children }) {
  return (
    <section className="tectonic-section">
      <div className="finding-eyebrow">{eyebrow}</div>
      <h2 className="tectonic-section-title">{title}</h2>
      {children}
    </section>
  );
}

function PositionCard({ pos, kind }) {
  return (
    <div className={`tectonic-position tectonic-position-${kind}`}>
      <div className="tectonic-position-head">
        <span className="tectonic-position-entity">
          {pos.entity}
          {pos.ticker && <span className="tectonic-position-ticker"> · {pos.ticker}</span>}
        </span>
        {pos.sizing && (
          <span className="tectonic-position-sizing">{pos.sizing}</span>
        )}
      </div>
      <div className="tectonic-position-thesis">{pos.thesis}</div>
      {pos.risk && (
        <div className="tectonic-position-risk">
          <span className="tectonic-position-risk-label">Risk</span> {pos.risk}
        </div>
      )}
    </div>
  );
}
